import { useV2GetChangeOrderByIdQuery } from "@generated";
import type { V2ChangeOrderResource } from "@generated";
import type { ChangeOrder, ChangeOrderStatus } from "../types";
import type { ChangeOrderCause } from "../constants/causes";

interface IncludedResource {
  id?: string;
  type?: string;
  attributes?: { name?: string } | Record<string, unknown>;
}

// The API serializes decimals as strings ("1000.0") and hours as either.
const num = (v: unknown) =>
  v === null || v === undefined || v === "" ? null : Number(v);

export const toChangeOrder = (
  resource: V2ChangeOrderResource,
  included: IncludedResource[] = []
): ChangeOrder => {
  const a = resource.attributes ?? {};
  const rel = resource.relationships ?? {};
  const userId = rel.user?.data?.id ? String(rel.user.data.id) : null;
  const user = userId
    ? included.find((i) => i.type === "users" && String(i.id) === userId)
    : undefined;
  const number = a.number ?? 0;

  return {
    id: String(resource.id),
    number,
    label: `CR-${number}`,
    cause: (a.cause ?? "") as ChangeOrderCause | "",
    causeNote: a["cause-note"] ?? "",
    externalReference: a["external-reference"] ?? "",
    externalUrl: a["external-url"] ?? "",
    status: a.status as ChangeOrderStatus,
    counts: !!a.counts,
    referenceOnly: !!a["reference-only"],
    revenue: num(a.revenue),
    cost: num(a.cost),
    hours: num(a.hours),
    createdAt: a["created-at"] ?? "",
    userId,
    userName: String((user?.attributes as { name?: string })?.name ?? ""),
    parentProjectId: rel["parent-project"]?.data?.id
      ? String(rel["parent-project"].data.id)
      : null,
    childProjectId: rel["child-project"]?.data?.id
      ? String(rel["child-project"].data.id)
      : null,
  };
};

export const useChangeOrder = (id: string) => {
  const { data, isLoading, error, refetch } = useV2GetChangeOrderByIdQuery(
    // parent-project must be included for its linkage to come back; Show
    // and Edit redirect when it doesn't match the route's project.
    { id, include: ["user", "parent-project", "child-project"] },
    { refetchOnMountOrArgChange: true }
  );

  const changeOrder = data?.data
    ? toChangeOrder(data.data, data.included as IncludedResource[])
    : null;

  return { changeOrder, isLoading, error, refetch };
};

export default useChangeOrder;
